import { createFileRoute, Link } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { PublicOrAppShell } from "@/components/public-or-app-shell";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — Cailève" },
      {
        name: "description",
        content:
          "How Cailève collects, stores, and protects your personal data.",
      },
      { property: "og:title", content: "Privacy Policy — Cailève" },
      {
        property: "og:description",
        content:
          "How Cailève collects, stores, and protects your personal data.",
      },
    ],
  }),
  component: () => (
    <PublicOrAppShell>
      <PrivacyPage />
    </PublicOrAppShell>
  ),
});

function PrivacyPage() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--caileve-bg)", color: "var(--caileve-ink)" }}>
      <div className="max-w-3xl mx-auto px-8 py-16">
        <Link
          to={user ? "/account" : "/login"}
          className="text-[11px] uppercase tracking-[0.25em] text-[var(--caileve-lilac-text)] hover:text-[var(--caileve-ink)]"
        >
          ← {user ? "Back to account" : "Back to sign in"}
        </Link>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--caileve-lilac-text)]">Legal</p>
      <h1
        className="font-serif-display text-5xl md:text-6xl mt-3 leading-[1.05]"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        Privacy Policy
      </h1>
      <p className="text-xs uppercase tracking-[0.25em] text-[var(--caileve-copper)] mt-6">
        Effective — July 6, 2026
      </p>

      <div className="flex justify-center my-12">
        <div className="w-16 h-px bg-[var(--caileve-copper)]" />
      </div>

      <Section title="A note on intention">
        Cailève, operated by Écrin Style LLC, is built as a personal sanctuary for your style. Your
        wardrobe, jewelry, and self-expression are deeply personal. This policy
        explains, plainly and honestly, what we collect, how we keep it safe,
        and the choices that always remain yours.
      </Section>

      <Section title="What we collect">
        <ul className="space-y-3 list-disc pl-5">
          <li>
            <strong>Profile information</strong> — your name, email address, and
            optional profile photo.
          </li>
          <li>
            <strong>Jewelry and clothing items</strong> — the details you choose
            to record, such as category, color, material, and notes.
          </li>
          <li>
            <strong>Photographs</strong> — images you upload of your pieces and
            looks, stored privately in encrypted storage.
          </li>
          <li>
            <strong>Saved looks</strong> — the compositions you create,
            including occasion, rating, and styling notes.
          </li>
          <li>
            <strong>Style requests</strong> — the inputs you provide when asking
            your concierge for a suggestion.
          </li>
          <li>
            <strong>Emotional feelings data</strong> — the optional reflections
            you tag to looks and requests describing how you wish to feel.
          </li>
          <li>
            <strong>Approximate location</strong> (optional, city-level only) — used solely to
            retrieve weather conditions when you request weather-aware styling.
            Never stored as a standalone record.
          </li>
        </ul>
      </Section>

      <Section title="How we store it">
        <p>
          All of your data is stored in private, encrypted databases on Lovable
          Cloud infrastructure. Photographs are kept in a private storage bucket
          accessible only through short-lived signed URLs issued to you.
        </p>
        <p>
          Row Level Security is enforced at the database level, meaning your
          rows are technically unreachable to any account other than your own —
          not visible to other users, not visible to administrators in the
          ordinary course of running the service.
        </p>
        <p>
          We do not sell or rent your personal data. Your photos, your jewelry, your
          looks — none of it is ever used to train our own models, or anyone else's.
        </p>
        <p>
          A few of Cailève's most personal features — scanning a piece, suggesting a
          look, your daily insight — work by asking Claude, the AI created by
          Anthropic, to actually do the thinking. When you use one of these, the
          relevant photo and a slice of your wardrobe context is sent to Anthropic
          for that one purpose, and nothing else. Under Anthropic's own commercial
          terms, they don't train on it, and it's automatically deleted from their
          systems within 30 days. Beyond this and the handful of service providers
          named below, your personal data doesn't leave Cailève.
        </p>
      </Section>

      <Section title="Security">
        <p>
          Écrin Style LLC uses industry-standard security measures including
          HTTPS for all traffic, Row Level Security policies on every table that
          holds personal data, private photo storage, and authenticated access
          for every read and write operation.
        </p>
      </Section>

      <Section title="Your rights">
        <p>
          You may delete your account and all associated data at any time from
          the My Account screen. To request deletion of your data outside the
          app, or to ask any question about how your information is handled,
          please write to{" "}
          <a
            href="mailto:hello@caileve.app"
            className="text-[var(--caileve-lilac)] hover:text-[var(--caileve-copper)] transition-colors"
          >
            hello@caileve.app
          </a>
          . For legal or administrative privacy requests (data access, deletion
          on behalf of a legal representative, regulatory inquiries), please write to{" "}
          <a
            href="mailto:admin@caileve.app"
            className="text-[var(--caileve-lilac)] hover:text-[var(--caileve-copper)] transition-colors"
          >
            admin@caileve.app
          </a>
          . We respond personally to every request.
        </p>
      </Section>

      <Section title="Changes to this policy">
        <p>
          If we ever change this policy, we will note the new date at the top
          and, where the changes are material, surface a notice in the app
          before the changes take effect.
        </p>
      </Section>

      <Section title="Third-party services">
        <p>
          Cailève uses the following third-party services to operate safely and
          improve your experience.
        </p>
        <p>
          Anthropic (Claude) — Cailève's styling intelligence is powered by
          Anthropic's Claude models. When you scan a piece, ask for a style
          suggestion, or open Cailève Eye, the photo and wardrobe context needed to
          answer you is sent to Anthropic for that single request. It is never used
          to train their models, and is deleted from their systems within 30 days
          under their commercial API terms. See anthropic.com/legal/commercial-terms.
        </p>
        <p>
          Sentry receives your anonymized user ID, browser version, and the
          page you were on when an error occurs. No collection data is
          included. See sentry.io/privacy.
        </p>
        <p>
          PostHog receives anonymized behavioral events such as when a look is
          saved or a style suggestion is requested. No collection data or
          personal details are included. See posthog.com/privacy.
        </p>
        <p>
          Stripe processes subscription payments and stores billing information
          under their own privacy policy. See stripe.com/privacy.
        </p>
        <p>
          OpenWeather — When you request weather-aware styling in Style Me or
          Cailève Eye, your approximate location (city-level, not precise GPS)
          is sent to OpenWeather to retrieve current weather conditions. This
          location is used only to generate the weather context for that styling
          request and is not stored by Cailève beyond the resulting look's
          conditions record.
        </p>
      </Section>

      <div className="flex justify-center my-12">
        <div className="w-16 h-px bg-[var(--caileve-copper)]" />
      </div>

      <div className="text-center space-y-3">
        <Link
          to="/terms"
          className="text-[var(--caileve-lilac)] hover:text-[var(--caileve-copper)] transition-colors text-sm"
        >
          Read our Terms of Service
        </Link>
        <p className="text-xs text-[var(--caileve-lilac)]">
          Copyright 2026 Écrin Style LLC. All rights reserved.
        </p>
      </div>
      </div>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-10">
      <h2
        className="font-serif-display text-2xl md:text-3xl mb-4 text-[var(--caileve-ink)]"
        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
      >
        {title}
      </h2>
      <div
        className="space-y-4 leading-relaxed text-[var(--caileve-ink)]/85"
        style={{
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          fontSize: "1.15rem",
        }}
      >
        {children}
      </div>
    </section>
  );
}